import React, { useState, useRef, useCallback, useEffect } from 'react';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { FormSearchInput, Button } from '../UI';
import type { AssetSearchResult, ComparisonEntity } from '@einhaus/project314-types';

interface ComparisonSearchProps {
    comparisonEntity: ComparisonEntity | null;
    onComparisonChange: (entity: ComparisonEntity | null) => void;
}

const ComparisonSearch: React.FC<ComparisonSearchProps> = ({ comparisonEntity, onComparisonChange }) => {
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<AssetSearchResult[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchResultsRef = useRef<HTMLDivElement>(null);

    const searchQuery = useAppQuery({
        ...trpc.search.search.queryOptions({ term: searchTerm }),
        enabled: !!searchTerm.trim(),
        staleTime: 30000
    });

    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            setShowSearchResults(false);
            setIsSearching(false);
            return;
        }

        setShowSearchResults(true);

        if (searchQuery.isLoading) {
            setIsSearching(true);
            return;
        }

        setIsSearching(false);

        if (searchQuery.data?.results) {
            setSearchResults(searchQuery.data.results);
            setShowSearchResults(true);
            setHighlightedIndex(-1);
        } else if (searchQuery.error) {
            setSearchResults([]);
            setShowSearchResults(true);
        }
    }, [searchQuery.data, searchQuery.error, searchQuery.isLoading, searchTerm]);

    const handleResultClick = useCallback(
        (result: AssetSearchResult) => {
            onComparisonChange({ id: result.id, type: result.type, symbol: result.symbol });
            setShowSearchResults(false);
            setInputValue('');
            setSearchTerm('');
        },
        [onComparisonChange]
    );

    const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setInputValue(value);

        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current);
        }

        if (!value.trim()) {
            setSearchTerm('');
            setShowSearchResults(false);
            setSearchResults([]);
            return;
        }

        searchDebounceTimerRef.current = setTimeout(() => {
            setSearchTerm(value);
            setShowSearchResults(true);
            setIsSearching(true);
        }, 300);
    }, []);

    const handleSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev >= searchResults.length - 1 ? 0 : prev + 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
                    break;
                case 'Enter':
                    e.preventDefault();

                    if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
                        handleResultClick(searchResults[highlightedIndex]);
                    }

                    break;
                case 'Escape':
                    e.preventDefault();
                    setInputValue('');
                    setSearchTerm('');
                    setShowSearchResults(false);
                    setHighlightedIndex(-1);
                    break;
            }
        },
        [searchResults, highlightedIndex, handleResultClick]
    );

    return (
        <div className="relative">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <FormSearchInput
                        showSearchIcon
                        aria-label="Search for entity to compare"
                        placeholder="Search for comparison asset..."
                        ref={searchInputRef}
                        value={inputValue}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                    />

                    {showSearchResults && inputValue.trim() && (
                        <div
                            className="max-h-[300px] overflow-y-auto absolute top-full left-0 right-0 z-10 bg-[#181818] border border-[#333] rounded-md mt-1 shadow-lg"
                            ref={searchResultsRef}
                        >
                            {searchQuery.isLoading || isSearching ? (
                                <div className="text-center p-3 text-zinc-400">Searching...</div>
                            ) : searchResults.length === 0 ? (
                                <div className="text-center p-3 text-zinc-400">No results found</div>
                            ) : (
                                <div>
                                    {searchResults.map((result, index) => (
                                        <div
                                            className={`p-2 hover:bg-zinc-700 cursor-pointer ${
                                                index === highlightedIndex ? 'bg-zinc-700' : ''
                                            }`}
                                            key={`${result.type}-${result.id}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleResultClick(result)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleResultClick(result);
                                                }
                                            }}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                        >
                                            <span className="font-medium block">{result.symbol || result.name}</span>
                                            <div className="text-sm text-zinc-400 flex justify-between">
                                                <span>{result.name}</span>
                                                <span>{result.type}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {isSearching && (
                    <div className="animate-spin h-5 w-5 ml-2 border-2 border-zinc-300 rounded-full border-t-blue-500" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                )}
            </div>

            {comparisonEntity && (
                <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-zinc-300">Comparing with:</span>
                    <Button size="sm" variant="dark" onClick={() => onComparisonChange(null)}>
                        <span className="mr-2">{comparisonEntity.symbol}</span>
                        <span className="text-xs">&times;</span>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ComparisonSearch;
